import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Group, Select, Switch, Table, Tabs, Text, Title } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Container, LayoutStandard, Loader, Modal, TextInput } from 'components';
import { IRootState } from 'config/redux';
import { AuthorityConstants, RouteConstants, ServiceConstants } from 'constant';
import { ProfileService } from 'services';
import { displayClasses, tableClasses } from 'styles';
import { AuthorityResponse, ProfileExtendedResponse } from 'types';
import { AppUtils } from 'utils';

const { 
  REQUEST_PENDING,
  REQUEST_PROFILES_LIST_ID, 
  REQUEST_PROFILES_PERMISSIONS_ID,
  REQUEST_PROFILES_CREATE_ID,
  REQUEST_PROFILES_UPDATE_ID,
  REQUEST_PROFILES_DELETE_ID,
} = ServiceConstants;

const TemplateFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'novi';

  // Redux selectors
  const profilesServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_LIST_ID]);
  const permissionsServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_PERMISSIONS_ID]);
  const createServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_CREATE_ID]);
  const updateServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_UPDATE_ID]);
  const deleteServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_PROFILES_DELETE_ID]);

  // Local state
  const [profiles, setProfiles] = useState<ProfileExtendedResponse[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<AuthorityResponse[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [baseTemplate, setBaseTemplate] = useState<string | null>(null);
  const [selectedAuthorities, setSelectedAuthorities] = useState<string[]>([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Load data on mount
  useEffect(() => {
    ProfileService.profilesAll({ page: 0, search: '', pageSize: 100 });
    ProfileService.profilePermissionsAll();
  }, []);

  // Handle profiles loaded
  useEffect(() => {
    AppUtils.resolveServiceObject(profilesServiceObject, REQUEST_PROFILES_LIST_ID, (data) => {
      setProfiles(data.content || data);
      // If editing existing template, find and load it
      if (!isNew && id) {
        const existingProfile = (data.content || data).find((p: ProfileExtendedResponse) => p.id === parseInt(id));
        if (existingProfile) {
          setTemplateName(existingProfile.name);
          // Ensure authorities are strings (codes), not objects
          const authCodes = (existingProfile.authorities || []).map((auth: any) => 
            typeof auth === 'string' ? auth : auth.code
          );
          setSelectedAuthorities(authCodes);
        }
      }
    });
  }, [profilesServiceObject, isNew, id]);

  // Handle permissions loaded
  useEffect(() => {
    AppUtils.resolveServiceObject(permissionsServiceObject, REQUEST_PROFILES_PERMISSIONS_ID, (data) => {
      if (data && data.length > 0) {
        setAvailablePermissions(data);
      }
    });
  }, [permissionsServiceObject]);

  // Handle create response
  useEffect(() => {
    AppUtils.resolveServiceObject(createServiceObject, REQUEST_PROFILES_CREATE_ID, () => {
      setIsSaving(false);
      navigate(RouteConstants.ROUTE_PERMISSIONS);
    }, () => {
      setIsSaving(false);
    });
  }, [createServiceObject, navigate]);

  // Handle update response
  useEffect(() => {
    AppUtils.resolveServiceObject(updateServiceObject, REQUEST_PROFILES_UPDATE_ID, () => {
      setIsSaving(false);
      navigate(RouteConstants.ROUTE_PERMISSIONS);
    }, () => {
      setIsSaving(false);
    });
  }, [updateServiceObject, navigate]);

  // Handle delete response
  useEffect(() => {
    AppUtils.resolveServiceObject(deleteServiceObject, REQUEST_PROFILES_DELETE_ID, () => {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      navigate(RouteConstants.ROUTE_PERMISSIONS);
    }, () => {
      setIsDeleting(false);
    });
  }, [deleteServiceObject, navigate]);

  const handlePermissionChange = (authorityName: string, enabled: boolean) => {
    if (enabled) {
      setSelectedAuthorities(prev => [...prev, authorityName]);
    } else {
      setSelectedAuthorities(prev => prev.filter(a => a !== authorityName));
    }
  };

  const handleBaseTemplateChange = (value: string | null) => {
    setBaseTemplate(value);
    if (value) {
      const sourceProfile = profiles.find(p => p.id === parseInt(value));
      if (sourceProfile) {
        // Ensure authorities are strings (codes), not objects
        const authCodes = (sourceProfile.authorities || []).map((auth: any) => 
          typeof auth === 'string' ? auth : auth.code
        );
        setSelectedAuthorities(authCodes);
      }
    }
  };

  const handleCancel = () => {
    navigate(RouteConstants.ROUTE_PERMISSIONS);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!id || isNew) return;
    setIsDeleting(true);
    ProfileService.profileDelete(parseInt(id));
  };

  // Generate code from name (e.g., "Test šablon" → "TEST_SABLON")
  const generateCodeFromName = (name: string): string => {
    return name
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^A-Z0-9\s]/g, '') // Keep only letters, numbers, spaces
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
  };

  const handleSave = () => {
    setIsSaving(true);
    // Ensure authorities are strings (codes), not objects
    const authorityCodes = selectedAuthorities.map(auth => 
      typeof auth === 'string' ? auth : (auth as any).code
    );
    const profileData = {
      code: generateCodeFromName(templateName),
      name: templateName,
      authorities: authorityCodes,
    };
    
    if (isNew) {
      ProfileService.profileCreate(profileData);
    } else if (id) {
      ProfileService.profileUpdate(parseInt(id), profileData);
    }
  };

  const isLoading = useCallback(() => {
    return (profilesServiceObject && profilesServiceObject.status === REQUEST_PENDING) ||
           (permissionsServiceObject && permissionsServiceObject.status === REQUEST_PENDING);
  }, [profilesServiceObject, permissionsServiceObject]);

  const templateOptions = useMemo(() => 
    profiles.map(p => ({ value: p.id.toString(), label: p.name })),
  [profiles]);

  const rows = useMemo(() => {
    return availablePermissions.map((permission: AuthorityResponse, index: number) => {
      const rowBg = index % 2 === 1 ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-gray-0)';
      const permissionCode = permission.code || permission.name || '';
      const isEnabled = selectedAuthorities.includes(permissionCode);
      return (
        <Table.Tr key={permissionCode}>
          <Table.Td style={{ backgroundColor: rowBg }}>{permission.description || permissionCode}</Table.Td>
          <Table.Td style={{ width: 100, textAlign: 'right', backgroundColor: rowBg }}>
            <Switch
              checked={isEnabled}
              onChange={(event) => handlePermissionChange(permissionCode, event.currentTarget.checked)}
              styles={{ track: { cursor: 'pointer' }, thumb: { cursor: 'pointer' } }}
            />
          </Table.Td>
        </Table.Tr>
      );
    });
  }, [availablePermissions, selectedAuthorities]);

  return (
    <LayoutStandard title={AppUtils.composeTabTitle(t('permissions.template.title'))} authority={AuthorityConstants.ROLE_PRF_CUD}>
      <Container>
        <Box pos="relative">
          <Loader overlay={true} visible={isLoading()} />
          <Tabs value="templates" variant="unstyled">
            <Tabs.List>
              <Tabs.Tab 
                value="templates" 
                className={displayClasses.tabList}
                onClick={() => navigate(RouteConstants.ROUTE_PERMISSIONS)}
              >
                {t('permissions.tabs.templates')}
              </Tabs.Tab>
              <Tabs.Tab 
                value="agents" 
                className={displayClasses.tabList}
                onClick={() => navigate(RouteConstants.ROUTE_PERMISSIONS + '?tab=agents')}
              >
                {t('permissions.tabs.agents')}
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Title order={3} mt="md" mb="md">
            {isNew ? t('permissions.template.new') : templateName}
          </Title>

          <Group align="flex-start" gap="md" mb="md">
            <TextInput
              label={t('permissions.template.name')}
              value={templateName}
              onChange={(event) => setTemplateName(event.currentTarget.value)}
              style={{ width: 200 }}
            />
            {isNew && (
              <Select
                label={t('permissions.template.applyExisting')}
                placeholder={t('select.something')}
                data={templateOptions}
                value={baseTemplate}
                onChange={handleBaseTemplateChange}
                clearable
                style={{ width: 200 }}
                rightSection={isSelectOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                onDropdownOpen={() => setIsSelectOpen(true)}
                onDropdownClose={() => setIsSelectOpen(false)}
              />
            )}
          </Group>

          <Table className={tableClasses.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('permissions.permission')}</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>{t('permissions.allowed')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>

          <Group justify="flex-end" mt="xl">
            {!isNew && (
              <Button variant="outline" color="red" radius="md" size="sm" fz={14} onClick={handleDeleteClick} style={{ cursor: 'pointer' }}>
                {t('action.delete')}
              </Button>
            )}
            <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={handleCancel} style={{ cursor: 'pointer' }}>
              {t('action.cancel')}
            </Button>
            <Button color="green" radius="md" size="sm" fz={14} onClick={handleSave} loading={isSaving} style={{ cursor: 'pointer' }}>
              {t('action.save')}
            </Button>
          </Group>
        </Box>

        {/* Delete confirmation modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={t('permissions.deleteModal.title')}
        >
          <Text mb="xl">{t('permissions.deleteModal.description')}</Text>
          <Group justify="center" gap="md">
            <Button variant="outline" color="dark" radius="md" size="sm" fz={14} onClick={() => setDeleteModalOpen(false)} style={{ cursor: 'pointer' }}>
              {t('action.cancel')}
            </Button>
            <Button color="red" radius="md" size="sm" fz={14} onClick={handleConfirmDelete} loading={isDeleting} style={{ cursor: 'pointer' }}>
              {t('action.delete')}
            </Button>
          </Group>
        </Modal>
      </Container>
    </LayoutStandard>
  );
};

export default TemplateFormPage;
